import React from "react";
import { useLocation } from "react-router-dom";
import { tokens } from "../../design/theme.next";
import { captureError, newRef } from "./tracking";

const role = tokens.colorRoles.light;

const styles = {
  page: {
    minHeight: "60vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: `${tokens.space[10]}px ${tokens.space[4]}px`,
    background: role.bg,
    fontFamily: tokens.fontFamily.sans,
    color: role.textPrimary,
  },
  full: { minHeight: "100vh" },
  card: {
    width: "100%",
    maxWidth: 440,
    background: role.surface,
    border: `1px solid ${role.border}`,
    borderRadius: tokens.radius.lg,
    boxShadow: tokens.elevation.sm,
    padding: `${tokens.space[8]}px ${tokens.space[6]}px`,
    textAlign: "center",
  },
  title: {
    margin: 0,
    fontSize: tokens.fontSize.xl,
    fontWeight: tokens.fontWeight.semibold,
    lineHeight: tokens.lineHeight.snug,
    letterSpacing: tokens.letterSpacing.tight,
  },
  body: {
    margin: `${tokens.space[3]}px 0 ${tokens.space[6]}px`,
    fontSize: tokens.fontSize.base,
    lineHeight: tokens.lineHeight.normal,
    color: role.textSecondary,
  },
  actions: { display: "flex", gap: tokens.space[3], justifyContent: "center", flexWrap: "wrap" },
  primary: {
    appearance: "none",
    border: 0,
    cursor: "pointer",
    background: tokens.palette.brandSolid[500],
    color: role.textOnBrand,
    borderRadius: tokens.radius.pill,
    padding: `${tokens.space[3]}px ${tokens.space[6]}px`,
    fontFamily: "inherit",
    fontSize: tokens.fontSize.sm,
    fontWeight: tokens.fontWeight.semibold,
  },
  secondary: {
    display: "inline-block",
    border: `1px solid ${role.borderStrong}`,
    color: role.textPrimary,
    textDecoration: "none",
    borderRadius: tokens.radius.pill,
    padding: `${tokens.space[3] - 1}px ${tokens.space[6]}px`,
    fontSize: tokens.fontSize.sm,
    fontWeight: tokens.fontWeight.semibold,
  },
  ref: { marginTop: tokens.space[6], fontSize: tokens.fontSize.xs, color: role.textTertiary, fontFamily: tokens.fontFamily.mono },
};

// A lazy chunk that fails to load after a deploy only recovers with a
// full reload, not a re-render.
function isChunkLoadError(error) {
  return /Loading (CSS )?chunk|ChunkLoadError|dynamically imported module/i.test(`${error && error.name} ${error && error.message}`);
}

export function ErrorFallback({ errorRef, onRetry, home = "/", fullPage = false }) {
  return (
    <div role="alert" style={{ ...styles.page, ...(fullPage ? styles.full : null) }}>
      <div style={styles.card}>
        <h1 style={styles.title}>Something broke on our side.</h1>
        <p style={styles.body}>Your answers are saved. Try again, or head back home and pick up where you left off.</p>
        <div style={styles.actions}>
          <button type="button" style={styles.primary} onClick={onRetry}>
            Try again
          </button>
          <a href={home} style={styles.secondary}>
            Go home
          </a>
        </div>
        {errorRef && <div style={styles.ref}>Ref: {errorRef}</div>}
      </div>
    </div>
  );
}

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, errorRef: null };
    this.retry = this.retry.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error, errorRef: newRef() };
  }

  componentDidCatch(error, info) {
    captureError(error, {
      ref: this.state.errorRef,
      scope: this.props.scope || "app",
      componentStack: info && info.componentStack ? info.componentStack.slice(0, 2000) : undefined,
    });
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null, errorRef: null });
    }
  }

  retry() {
    if (isChunkLoadError(this.state.error)) {
      window.location.reload();
      return;
    }
    this.setState({ error: null, errorRef: null });
  }

  render() {
    if (this.state.error) {
      return <ErrorFallback errorRef={this.state.errorRef} onRetry={this.retry} home={this.props.home} fullPage={this.props.fullPage} />;
    }
    return this.props.children;
  }
}

// Resets itself when the route changes, so navigating away from a broken
// page clears the fallback.
export function RouteErrorBoundary({ children, ...props }) {
  const location = useLocation();
  return (
    <ErrorBoundary resetKey={location.pathname} {...props}>
      {children}
    </ErrorBoundary>
  );
}

export default ErrorBoundary;
